import { Box, Typography, Button, SxProps, Theme } from "@mui/material";
import { Code, People } from "@mui/icons-material";
import { SectionContainer } from "../../../modules/SectionContainer/SectionContainer";
import { SectionTitle } from "../../../modules/SectionTitle/SectionTitle";
import { SectionSubtitle } from "../../../modules/SectionSubtitle/SectionSubtitle";

const phases = [
  {
    number: 1,
    title: "まず動くものを作れて、一人で動けるようになる",
    tech: {
      label: "技術",
      summary: "問題なく動くものができる",
      content:
        "エラーなく仕様通りに実装し、テスト・リリースまでやり切れる。作業の見積もりとスケジュールを自分で立てられる。",
    },
    management: {
      label: "マネジメント",
      summary: "自分にできる",
      content:
        "誰かに言われなくても自分で考えて動ける。自分のタスク・時間・品質を自分でコントロールできる。指示待ちから脱却し、自走できる。",
    },
  },
  {
    number: 2,
    title: "チームの中で役割を担えるようになる",
    tech: {
      label: "技術",
      summary: "設計思想を意識し動くものができる",
      content:
        "設計思想を理解し、「なぜその設計にするのか」を意識してコードを書ける。ただ動くだけでなく、正しい設計で動くものを作れる。",
    },
    management: {
      label: "マネジメント",
      summary: "他人にできる",
      content:
        "後輩への指導・作業の依頼・進捗サポートができる。教える・任せる・フォローするができる、マネージャーの一歩手前の状態。",
    },
  },
  {
    number: 3,
    title: "クライアントの要望を実現し、大規模チームを動かせるようになる",
    tech: {
      label: "技術",
      summary: "設計思想を定義し作ることができる",
      content: "自らプロジェクトの設計思想を定義し、ドキュメント化してチーム全体の開発をリードできる。",
    },
    management: {
      label: "マネジメント",
      summary: "チームにできる",
      content:
        "クライアントの要望をヒアリング・整理・実現できる。複数人のチームを動かし、組織として成果を出せる。優秀な方はチームリーダーとして部下を持ち、より大きな責任と実績を積んでいきます。",
    },
  },
];

export const GrowthSection = () => {
  const scrollToRequirements = () => {
    const element = document.getElementById("requirements-section");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <SectionContainer sx={{ background: "#FFFFFF", padding: { md: "100px 0", xs: "60px 0" } }}>
      <SectionTitle sx={{ fontSize: { md: "36px", xs: "24px" } }}>
        テクノスタンダードが考える、
        <br />
        優秀なエンジニアへの道筋
      </SectionTitle>
      <SectionSubtitle sx={{ maxWidth: "700px" }}>
        「なんとなく経験を積む」ではなく、明確な3つのフェーズで成長を定義しています。今自分がどこにいて、次に何をすべきかが常にわかる環境を用意しています。
      </SectionSubtitle>

      <Box sx={cardsContainerSx}>
        {phases.map((phase) => (
          <Box key={phase.number} sx={cardSx}>
            <Box sx={cardHeaderSx}>
              <Typography sx={phaseNumberSx}>フェーズ {phase.number}</Typography>
              <Typography sx={phaseTitleSx}>{phase.title}</Typography>
            </Box>

            <Box sx={axisContainerSx}>
              {/* 技術 */}
              <Box sx={techBoxSx}>
                <Box sx={axisHeaderSx}>
                  <Code sx={{ fontSize: 18, color: "#00BFA5" }} />
                  <Typography sx={axisLabelSx}>{phase.tech.label}</Typography>
                </Box>
                <Typography sx={axisSummarySx}>{phase.tech.summary}</Typography>
                <Typography sx={axisContentSx}>{phase.tech.content}</Typography>
              </Box>

              {/* マネジメント */}
              <Box sx={managementBoxSx}>
                <Box sx={axisHeaderSx}>
                  <People sx={{ fontSize: 18, color: "#22A1B9" }} />
                  <Typography sx={axisLabelSx}>{phase.management.label}</Typography>
                </Box>
                <Typography sx={axisSummarySx}>{phase.management.summary}</Typography>
                <Typography sx={axisContentSx}>{phase.management.content}</Typography>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>

      <Box sx={highlightBoxSx}>
        <Typography sx={highlightTextSx}>
          フェーズ3を超えられるエンジニアは、市場で本当に希少な存在になれます。
          <br />
          本物の実績を作るその道筋が、テクノスタンダードには用意されています。
        </Typography>
      </Box>

      <Box sx={ctaContainerSx}>
        <Button sx={ctaButtonSx} onClick={scrollToRequirements}>
          エントリーする
        </Button>
      </Box>
    </SectionContainer>
  );
};

const cardsContainerSx: SxProps<Theme> = {
  display: "grid",
  gridTemplateColumns: { md: "repeat(3, 1fr)", xs: "1fr" },
  gap: { md: "24px", xs: "20px" },
  marginTop: { md: "48px", xs: "32px" },
  alignItems: "stretch",
};

const cardSx: SxProps<Theme> = {
  background: "#f8f9fa",
  borderRadius: "12px",
  padding: { md: "28px", xs: "20px" },
  display: "flex",
  flexDirection: "column",
};

const cardHeaderSx: SxProps<Theme> = {
  marginBottom: { md: "20px", xs: "16px" },
  paddingBottom: { md: "16px", xs: "12px" },
  borderBottom: "2px solid #00BFA5",
  minHeight: { md: "80px", xs: "auto" },
};

const phaseNumberSx: SxProps<Theme> = {
  fontSize: { md: "14px", xs: "12px" },
  fontWeight: 700,
  color: "#00BFA5",
  letterSpacing: "0.1em",
  marginBottom: "8px",
};

const phaseTitleSx: SxProps<Theme> = {
  fontSize: { md: "16px", xs: "15px" },
  fontWeight: 700,
  color: "#222222",
  lineHeight: 1.5,
};

const axisContainerSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  flex: 1,
};

const axisBoxBaseSx: SxProps<Theme> = {
  background: "#FFFFFF",
  borderRadius: "8px",
  padding: { md: "16px", xs: "14px" },
  border: "1px solid #E0E0E0",
  display: "flex",
  flexDirection: "column",
};

const techBoxSx: SxProps<Theme> = {
  ...axisBoxBaseSx,
  minHeight: { md: "180px", xs: "auto" },
};

const managementBoxSx: SxProps<Theme> = {
  ...axisBoxBaseSx,
  minHeight: { md: "220px", xs: "auto" },
};

const axisHeaderSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  marginBottom: "8px",
};

const axisLabelSx: SxProps<Theme> = {
  fontSize: { md: "14px", xs: "13px" },
  fontWeight: 700,
  color: "#222222",
};

const axisSummarySx: SxProps<Theme> = {
  fontSize: { md: "13px", xs: "12px" },
  fontWeight: 600,
  color: "#22A1B9",
  marginBottom: "8px",
};

const axisContentSx: SxProps<Theme> = {
  fontSize: { md: "13px", xs: "12px" },
  color: "#666666",
  lineHeight: 1.7,
};

const highlightBoxSx: SxProps<Theme> = {
  marginTop: { md: "48px", xs: "32px" },
  padding: { md: "40px", xs: "24px" },
  background: "linear-gradient(135deg, rgba(34, 161, 185, 0.1), rgba(0, 191, 165, 0.1))",
  borderRadius: "12px",
  textAlign: "center",
};

const highlightTextSx: SxProps<Theme> = {
  fontSize: { md: "20px", xs: "16px" },
  fontWeight: 600,
  color: "#222222",
  lineHeight: 1.8,
};

const ctaContainerSx: SxProps<Theme> = {
  marginTop: { md: "32px", xs: "24px" },
  textAlign: "center",
};

const ctaButtonSx: SxProps<Theme> = {
  background: "linear-gradient(135deg, #22A1B9, #00BFA5)",
  color: "#FFFFFF",
  fontWeight: 700,
  padding: { md: "16px 48px", xs: "14px 40px" },
  fontSize: { md: "16px", xs: "15px" },
  borderRadius: "8px",
  textTransform: "none",
  transition: "all 0.3s",
  boxShadow: "0 4px 15px rgba(34, 161, 185, 0.3)",
  "&:hover": {
    background: "linear-gradient(135deg, #1d8fa3, #00a890)",
    transform: "translateY(-2px)",
    boxShadow: "0 8px 25px rgba(34, 161, 185, 0.4)",
  },
};
